import {
  SessionFragment,
  TrainingsSessionAttendanceStatusEnum,
  TrainingsSessionModalityEnum,
  TrainingsTrainingMembershipStatusEnum,
} from 'generated/resources/graphql'
import { uniq } from 'lodash'

import i18n from 'modules/core/lib/i18n'

export type TrainingAttendanceStatus =
  | 'notstarted'
  | 'started'
  | 'partiallycompleted'
  | 'completed'
  | 'missing'
  | 'notassigned'

const getTrainingAttendanceStatus = (
  statuses: TrainingsSessionAttendanceStatusEnum[]
): TrainingsTrainingMembershipStatusEnum => {
  const uniqueStatuses = [...new Set(statuses)]

  if (uniqueStatuses.length === 1) {
    const status = uniqueStatuses[0]
    if (status === 'completed') {
      return 'completed'
    } else if (status === 'missing') {
      return 'missing'
    } else if (status === 'pending') {
      return 'notstarted'
    }
  }
  if (uniqueStatuses.includes('inprogress')) {
    return 'started'
  }

  return 'partiallycompleted'
}

export const useTrainingsDatatable = () => {
  const getModalityTranslation = (modality?: TrainingsSessionModalityEnum | null) => {
    switch (modality) {
      case 'inperson':
        return i18n.t('trainings.my_trainings_revamp.overview.modalities.inperson')
      case 'mixed':
        return i18n.t('trainings.my_trainings_revamp.overview.modalities.mixed')
      case 'online':
        return i18n.t('trainings.my_trainings_revamp.overview.modalities.online')
      default:
        return i18n.t('trainings.my_trainings_revamp.overview.modalities.none')
    }
  }
  const getModality = (
    modality: TrainingsSessionModalityEnum | null | undefined
  ): TrainingsSessionModalityEnum | undefined => {
    if (!modality) {
      return undefined
    }

    return modality
  }

  const getSessionAttendanceTranslation = (status?: TrainingsSessionAttendanceStatusEnum) => {
    switch (status) {
      case 'pending':
        return i18n.t('trainings.my_trainings_revamp.session_attendance_status.not_started')
      case 'completed':
        return i18n.t('trainings.my_trainings_revamp.session_attendance_status.attended')
      case 'inprogress':
        return i18n.t('trainings.my_trainings_revamp.session_attendance_status.in_progress')
      case 'missing':
        return i18n.t('trainings.my_trainings_revamp.session_attendance_status.did_not_attend')
      default:
        throw new Error(`Unknown session attendance status: ${status}`)
    }
  }
  const getTrainingAttendanceTranslation = (status: TrainingsTrainingMembershipStatusEnum) => {
    switch (status) {
      case 'notstarted':
        return i18n.t('trainings.my_training.all_courses_table.not_started')
      case 'completed':
        return i18n.t('trainings.my_training.all_courses_table.completed')
      case 'started':
        return i18n.t('trainings.my_training.all_courses_table.started')
      case 'partiallycompleted':
        return i18n.t('trainings.my_training.all_courses_table.partially_completed')
      case 'missing':
        return i18n.t('trainings.my_training.all_courses_table.absent')
      case 'notassigned':
        return i18n.t('trainings.my_training.all_courses_table.not_assigned')
      default:
        throw new Error(`Unknown training attendance status: ${status}`)
    }
  }

  function parseAttendanceStatus(status: string): TrainingsSessionAttendanceStatusEnum {
    switch (status) {
      case 'pending':
      case 'inprogress':
      case 'completed':
      case 'missing':
        return status
      default:
        throw new Error(`Unknown session attendance status: ${status}`)
    }
  }

  const getSessionAttendanceColor = (status: TrainingsSessionAttendanceStatusEnum) => {
    switch (status) {
      case 'completed':
        return 'lime'
      case 'inprogress':
        return 'malibu'
      case 'missing':
        return 'radical'
      case 'pending':
        return 'neutral'
      default:
        throw new Error(`Unknown session attendance status: ${status}`)
    }
  }

  const getTrainingAttendanceColor = (status: TrainingsTrainingMembershipStatusEnum) => {
    switch (status) {
      case 'partiallycompleted':
        return 'champagne'
      case 'completed':
        return 'lime'
      case 'started':
        return 'malibu'
      case 'missing':
        return 'radical'
      case 'notassigned':
        return 'tangerine'
      case 'notstarted':
        return 'neutral'
      default:
        throw new Error(`Unknown training attendance status: ${status}`)
    }
  }

  const getDataCollectionSessionAttendanceColor = (
    status?: TrainingsSessionAttendanceStatusEnum
  ) => {
    switch (status) {
      case 'completed':
        return 'positive'
      case 'inprogress':
        return 'info'
      case 'missing':
        return 'critical'
      case 'pending':
        return 'neutral'
      default:
        throw new Error(`Unknown session attendance status: ${status}`)
    }
  }

  const getDataCollectionTrainingAttendanceColor = (
    status?: TrainingsTrainingMembershipStatusEnum
  ) => {
    switch (status) {
      case 'completed':
        return 'positive'
      case 'started':
        return 'info'
      case 'partiallycompleted':
        return 'warning'
      case 'missing':
        return 'critical'
      case 'notassigned':
        return 'warning'
      case 'notstarted':
        return 'neutral'
      default:
        return 'neutral'
    }
  }

  const collectModalities = (sessions: SessionFragment[]) => {
    const modalities = sessions
      .map((session) => session.modality)
      .filter(Boolean)
      .map((modality) => getModalityTranslation(modality))

    return Array.from(new Set(modalities)).join(', ')
  }

  const getModalityValue = (
    modalities: (TrainingsSessionModalityEnum | undefined)[]
  ): TrainingsSessionModalityEnum | undefined => {
    return uniq(modalities).length === 1 && modalities[0] ? modalities[0] : undefined
  }

  return {
    collectModalities,
    getSessionAttendanceColor,
    getTrainingAttendanceColor,
    getSessionAttendanceTranslation,
    getTrainingAttendanceTranslation,
    getModalityTranslation,
    getDataCollectionSessionAttendanceColor,
    getDataCollectionTrainingAttendanceColor,
    getModality,
    getTrainingAttendanceStatus,
    getModalityValue,
    parseAttendanceStatus,
  }
}
